/* media-ready.js — catalogue tiles arrive in ONE frame, never as a top-down wipe.
 *
 * WHY: Shopify's CDN streams WebP/AVIF and browsers paint those row-by-row as
 * bytes land, so on a slow line a tile visibly wipes downward for a second or
 * more. The A-2 reference (Athlete2) hides that by collapsing the <img> to a
 * 44x3 sliver behind an indeterminate bar and snapping it back on `load`.
 *
 * We take the same BEHAVIOUR in our own idiom: theme.css holds .card__media at
 * opacity 0 behind the very loadbar the quick view uses, and this file adds
 * `.is-ready` once the bitmap has actually DECODED — so the reveal is a single
 * clean frame, matching the theme's hold-then-cut view-transition language.
 * Holding opacity rather than geometry means we keep native loading/eager,
 * fetchpriority and srcset, all of which the data-src approach throws away.
 *
 * Contract with theme.css:
 *   .js .card__media:not(.is-ready) > img:not(.card__media-alt) { opacity: 0 }
 *   .js .card__media:not(.is-ready)::after { …loadbar… }
 *   .card__media-alt.is-ready                — hover frame, gated the same way
 * theme.css also carries a pure-CSS 12s failsafe reveal, so a missing or broken
 * bundle can never leave a tile permanently blank behind a spinner.
 *
 * Renderer-agnostic on purpose: SSR (snippets/product-card.liquid), the vanilla
 * Typesense grid (typesense-plp.js) and the React island (src/islands/plp/Tile.jsx)
 * all emit the same .card__media contract, so none of them needs markup changes.
 */
(() => {
  'use strict';

  var BOX = '.card__media';
  var HOLD_MS = 8000;   // watchdog once a held tile is actually on screen
  var armed = 'WeakSet' in window ? new WeakSet() : null;

  function reveal(box) {
    if (box && box.classList && !box.classList.contains('is-ready')) box.classList.add('is-ready');
  }

  // decode() resolves when the bitmap is ready to paint with no partial rows.
  // It rejects on a src swapped mid-flight (swatch hover, hover carousel) and on
  // broken images — reveal either way rather than holding a spinner forever.
  function settle(img) {
    var box = img.closest && img.closest(BOX);
    if (!box) return;
    if (img.classList.contains('card__media-alt')) { img.classList.add('is-ready'); return; }
    if (img.decode) img.decode().then(function () { reveal(box); }, function () { reveal(box); });
    else reveal(box);
  }

  // A held tile that is on screen gets a deadline. Off-screen tiles are not
  // loading yet (loading="lazy" + content-visibility:auto), so timing them from
  // page load would reveal empty frames for no reason.
  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var box = entries[i].target;
        io.unobserve(box);
        (function (b) { setTimeout(function () { reveal(b); }, HOLD_MS); })(box);
      }
    }, { rootMargin: '100px' });
  }

  function watch(box) {
    if (armed) { if (armed.has(box)) return; armed.add(box); }
    else if (box.dataset.mrArmed) return; else box.dataset.mrArmed = '1';
    if (io) io.observe(box);
    else setTimeout(function () { reveal(box); }, HOLD_MS);
  }

  function sweep() {
    var boxes = document.querySelectorAll(BOX + ':not(.is-ready)');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var img = box.querySelector('img:not(.card__media-alt)');
      if (!img) { reveal(box); continue; }            // placeholder tile — nothing to wait for
      if (img.complete) { img.naturalWidth > 0 ? settle(img) : reveal(box); }
      else watch(box);
    }
  }

  // `load`/`error` do not bubble, but they DO capture — one pair of listeners
  // therefore covers server-rendered tiles and everything the two client grids
  // inject later, with no per-image wiring.
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && t.tagName === 'IMG' && t.closest && t.closest(BOX)) settle(t);
  }, true);
  document.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'IMG' || !t.closest) return;
    var box = t.closest(BOX);
    if (box) { t.classList.add('is-ready'); reveal(box); }
  }, true);

  sweep();
  document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
  window.addEventListener('pageshow', sweep);        // bfcache restore

  // client grids replace the whole hit list on every facet/sort/page change;
  // the capture listeners above reveal those tiles, this only re-arms the
  // watchdog for images that never fire either event.
  if ('MutationObserver' in window) {
    var t = 0;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) if (records[i].addedNodes.length) {
        clearTimeout(t);
        t = setTimeout(sweep, 200);
        return;
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
